import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";

const SCRIPT_ID = "google-recaptcha-v2-script";
const DEFAULT_TEST_SITE_KEY = "6Lf9iIotAAAAAJVObwE_C0NlBLlDDJgnxPP8k6Ia";

// Helper to load reCAPTCHA script only once and queue readiness callbacks
let isScriptLoading = false;
const readyCallbacks = [];

const loadRecaptchaScript = (onReady) => {
  if (
    typeof window !== "undefined" &&
    window.grecaptcha &&
    window.grecaptcha.render
  ) {
    onReady();
    return;
  }

  readyCallbacks.push(onReady);

  if (isScriptLoading) return;

  if (document.getElementById(SCRIPT_ID)) {
    isScriptLoading = true;
    return;
  }

  isScriptLoading = true;

  window.__onRecaptchaLoaded__ = () => {
    isScriptLoading = false;
    while (readyCallbacks.length > 0) {
      const cb = readyCallbacks.shift();
      if (typeof cb === "function") cb();
    }
  };

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src =
    "https://google.com/recaptcha/api.js?onload=__onRecaptchaLoaded__&render=explicit";
  script.async = true;
  script.defer = true;
  script.onerror = () => {
    console.error("Failed to load Google reCAPTCHA script.");
    isScriptLoading = false;
  };
  document.head.appendChild(script);
};

const ReCaptcha = forwardRef(
  (
    {
      siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
        DEFAULT_TEST_SITE_KEY,
      theme = "light",
      size = "normal",
      onChange,
      onExpired,
      onError,
      errorMessage,
      className = "",
    },
    ref,
  ) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    const handleCallback = useCallback(
      (token) => {
        if (onChange) onChange(token);
      },
      [onChange],
    );

    const handleExpired = useCallback(() => {
      if (onExpired) onExpired();
      if (onChange) onChange("");
    }, [onExpired, onChange]);

    const handleError = useCallback(() => {
      if (onError) onError();
    }, [onError]);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          if (
            typeof window !== "undefined" &&
            window.grecaptcha &&
            widgetIdRef.current !== null
          ) {
            try {
              window.grecaptcha.reset(widgetIdRef.current);
            } catch (err) {
              console.warn("reCAPTCHA reset warning:", err);
            }
          }
        },
        getResponse: () => {
          if (
            typeof window !== "undefined" &&
            window.grecaptcha &&
            widgetIdRef.current !== null
          ) {
            try {
              return window.grecaptcha.getResponse(widgetIdRef.current);
            } catch (err) {
              return "";
            }
          }
          return "";
        },
      }),
      [],
    );

    useEffect(() => {
      let isMounted = true;

      loadRecaptchaScript(() => {
        if (
          !isMounted ||
          !containerRef.current ||
          widgetIdRef.current !== null
        ) {
          return;
        }

        try {
          // Clear container before rendering just in case
          containerRef.current.innerHTML = "";
          const widgetId = window.grecaptcha.render(containerRef.current, {
            sitekey: siteKey,
            theme: theme,
            size: size,
            callback: handleCallback,
            "expired-callback": handleExpired,
            "error-callback": handleError,
          });
          widgetIdRef.current = widgetId;
        } catch (error) {
          console.warn("reCAPTCHA rendering note:", error);
        }
      });

      return () => {
        isMounted = false;
      };
    }, [siteKey, theme, size, handleCallback, handleExpired, handleError]);

    return (
      <div className={`recaptcha-container ${className}`}>
        <div
          ref={containerRef}
          className="inline-block min-h-[78px] min-w-[304px] overflow-hidden rounded"
        />
        {errorMessage && (
          <p className="mt-1.5 text-xs text-red-500 font-medium flex items-center gap-1">
            <svg
              className="w-3.5 h-3.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {errorMessage}
          </p>
        )}
      </div>
    );
  },
);

ReCaptcha.displayName = "ReCaptcha";

export default ReCaptcha;
